import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import RichTextEditor from "../../components/admin/RichTextEditor";
import ImageUploader from "../../components/admin/ImageUploader";

const CATEGORIES = [
  "Educational Insights",
  "Faculty Corner",
  "Parent Zone",
  "Founder's Journal",
];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .slice(0, 80);
}

const EMPTY = {
  title: "",
  slug: "",
  category: "Educational Insights",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "Perforange Editorial",
  read_minutes: 5,
  in_spotlight: false,
};

export default function AdminBlogEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/blog/posts/${slug}`);
        if (alive) setForm({ ...EMPTY, ...data });
        setSlugTouched(true);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/admin/blog", { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug, isNew, navigate]);

  // auto-slug from title when creating
  useEffect(() => {
    if (isNew && !slugTouched) {
      setForm((f) => ({ ...f, slug: slugify(f.title) }));
    }
  }, [form.title, isNew, slugTouched]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onSave = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!form.slug.trim()) return toast.error("Slug is required.");
    if (!form.excerpt.trim()) return toast.error("Excerpt is required.");
    if (!form.content.trim() || form.content === "<p></p>") return toast.error("Content cannot be empty.");

    const payload = {
      ...form,
      slug: slugify(form.slug),
      read_minutes: Number(form.read_minutes) || 5,
    };

    setSaving(true);
    try {
      if (isNew) {
        await api.post("/blog/posts", payload);
        toast.success("Post created.");
      } else {
        await api.put(`/blog/posts/${slug}`, payload);
        toast.success("Post updated.");
      }
      navigate("/admin/blog", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-16 flex items-center justify-center text-[#A1A1AA]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading post…
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <Link to="/admin/blog" className="inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase tracking-widest text-[#A1A1AA] hover:text-white mb-3" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4" /> Back to posts
          </Link>
          <h1 className="font-display font-black text-3xl md:text-4xl text-white">
            {isNew ? "New Post" : "Edit Post"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Link
              to={`/blog/${slug}`}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-xs font-bold text-[#D4D4D8] hover:bg-white/10"
              data-testid="admin-view-live"
            >
              View live <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="admin-save"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#FF8C00] text-[#0A0A0C] text-sm font-black uppercase tracking-widest hover:bg-[#FFA333] disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? "Publish" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-6">
          <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-6 space-y-4">
            <Field label="Title *" testId="field-title">
              <input
                type="text"
                value={form.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="How a CCL becomes a Tier-1 feeder…"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-base"
              />
            </Field>

            <Field label="Slug (URL) *" testId="field-slug" hint="Used in the URL, e.g. /blog/your-slug. Auto-generated from the title when creating.">
              <input
                type="text"
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); update({ slug: e.target.value }); }}
                placeholder="auto-generated-from-title"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm font-mono-tech"
              />
            </Field>

            <Field label="Excerpt *" testId="field-excerpt" hint="Shown on the blog grid + at the top of the post.">
              <textarea
                rows={3}
                value={form.excerpt}
                onChange={(e) => update({ excerpt: e.target.value })}
                placeholder="A single line that sells the article."
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm resize-none"
              />
            </Field>
          </div>

          <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-6">
            <p className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-3">Content *</p>
            <RichTextEditor
              value={form.content}
              onChange={(html) => update({ content: html })}
              placeholder="Start writing…"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-6">
            <ImageUploader
              value={form.cover_image}
              onChange={(v) => update({ cover_image: v })}
            />
          </div>

          <div className="rounded-2xl border border-white/8 bg-[#0C0C10] p-6 space-y-4">
            <Field label="Category *" testId="field-category">
              <select
                value={form.category}
                onChange={(e) => update({ category: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF8C00] text-sm appearance-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23FF8C00' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} className="bg-[#0C0C10]">{c}</option>
                ))}
              </select>
            </Field>

            <Field label="Author" testId="field-author">
              <input
                type="text"
                value={form.author}
                onChange={(e) => update({ author: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF8C00] text-sm"
              />
            </Field>

            <Field label="Read time (minutes)" testId="field-read-min">
              <input
                type="number"
                min={1}
                value={form.read_minutes}
                onChange={(e) => update({ read_minutes: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FF8C00] text-sm"
              />
            </Field>

            <div className="pt-2 border-t border-white/8">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={!!form.in_spotlight}
                  onChange={(e) => update({ in_spotlight: e.target.checked })}
                  data-testid="field-spotlight"
                  className="sr-only"
                />
                <span
                  className={`shrink-0 mt-0.5 w-10 h-6 rounded-full transition-colors relative ${
                    form.in_spotlight ? "bg-[#FF8C00]" : "bg-white/10"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      form.in_spotlight ? "translate-x-[18px]" : "translate-x-0.5"
                    }`}
                  />
                </span>
                <div>
                  <p className="text-sm font-bold text-white flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#FF8C00]" /> In Spotlight
                  </p>
                  <p className="text-xs text-[#A1A1AA] mt-0.5">
                    Pin this post to the hero of the blog page. Only one post can hold the spotlight at a time.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, testId, children }) {
  return (
    <div data-testid={testId}>
      <label className="text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">{label}</label>
      {children}
      {hint && <p className="mt-1.5 text-[11px] text-[#6a6a70]">{hint}</p>}
    </div>
  );
}
